import {
    HttpContext,
    HttpContextToken,
    HttpEventType,
    HttpHandler,
    HttpInterceptor,
    HttpProgressEvent,
    HttpRequest,
} from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { AppConfig } from "app/common/app-config";
import { formatBytes } from "app/common/utils";
import { StorageClient } from "app/dal/generated/clients";
import { map, Observable, tap, throwError } from "rxjs";
import { EntityAttachmentSensitivity } from "./entity-attachment-metadata";
import { EntityAttachmentService } from "./entity-attachment.service";
import { OrchestrateBlobMetadata } from "./orchestrate-blob-metadata";

export interface UploadBlobDto<TAccess = object | undefined, TLink = object | undefined> {
    storageStrategyName: string;
    accessData: TAccess;
    linkData: TLink;
}

export enum UploadStatus {
    Pending,
    InProgress,
    Done,
}

export interface UploadProgress {
    status: Exclude<UploadStatus, UploadStatus.Done>;
    progress?: number;
}

export interface UploadResult<TAccess = object | undefined> {
    status: UploadStatus.Done;
    progress: 100;
    result: OrchestrateBlobMetadata<TAccess>;
}

@Injectable({ providedIn: "root" })
export class StorageService {
    private storageClient = inject(StorageClient);
    private appConfig = inject(AppConfig);

    public uploadBlob<TAccess = object | undefined, TLink = object | undefined>(
        dto: UploadBlobDto<TAccess, TLink>,
        file: File,
    ): Observable<UploadProgress | UploadResult<TAccess>> {
        if (file.size > this.appConfig.maxUploadSizeBytes) {
            const formattedMaxSize = formatBytes(this.appConfig.maxUploadSizeBytes, 0);
            return throwError(() => new Error(`File cannot be larger than ${formattedMaxSize}`));
        }

        return new Observable((subscriber) => {
            subscriber.next({ status: UploadStatus.Pending });

            const context = new HttpContext().set(UPLOAD_PROGRESS_LISTENER, (event) =>
                subscriber.next({ status: UploadStatus.InProgress, progress: toPercent(event) }),
            );
            return this.storageClient
                .uploadBlob(
                    {
                        storageStrategyName: dto.storageStrategyName,
                        accessData: JSON.stringify(dto.accessData ?? null),
                        linkData: JSON.stringify(dto.linkData ?? null),
                        file,
                    },
                    context,
                )
                .pipe(
                    map((result): UploadResult<TAccess> => ({
                        status: UploadStatus.Done,
                        progress: 100,
                        result,
                    })),
                )
                .subscribe(subscriber);
        });
    }

    public listBlobsLinkedBy<TAccess = object | undefined>(
        storageStrategyName: string,
        linkData?: object,
    ) {
        return this.storageClient
            .listBlobs({ storageStrategyName, linkData: JSON.stringify(linkData) })
            .pipe(map((blobs) => blobs as OrchestrateBlobMetadata<TAccess>[]));
    }

    public getBlobContent(blobId: string) {
        return this.storageClient.getBlobContent(blobId);
    }

    public updateAccessData(blobId: string, accessData: object) {
        return this.storageClient.updateAccessData(blobId, accessData);
    }

    public delete(blobId: string) {
        return this.storageClient.deleteBlob(blobId);
    }

    public buildEntityAttachmentService(
        storageStrategyName: string,
        entityId: number,
        defaultSensitivity: EntityAttachmentSensitivity,
    ) {
        return new EntityAttachmentService(this, storageStrategyName, entityId, defaultSensitivity);
    }
}

/** Undefined when the total size is unknown or the upload is awaiting a response */
function toPercent(event: HttpProgressEvent) {
    return event.total && event.total !== event.loaded
        ? Math.round((100 * event.loaded) / event.total)
        : undefined;
}

/** Called with a request's upload progress, as the generated API client only emits the response */
const UPLOAD_PROGRESS_LISTENER = new HttpContextToken<
    ((event: HttpProgressEvent) => void) | undefined
>(() => undefined);

@Injectable()
export class UploadProgressInterceptor implements HttpInterceptor {
    public intercept(req: HttpRequest<unknown>, next: HttpHandler) {
        const listener = req.context.get(UPLOAD_PROGRESS_LISTENER);
        if (!listener) {
            return next.handle(req);
        }

        return next.handle(req.clone({ reportProgress: true })).pipe(
            tap((event) => {
                if (event.type === HttpEventType.UploadProgress) {
                    listener(event);
                }
            }),
        );
    }
}
